/**
 * The dashboard's settings panel, behind the gear beside its name.
 *
 * Deliberately short: the switches that change how the whole dashboard behaves,
 * and delete. The name is edited by clicking it in the toolbar.
 *
 * Settings belong to the dashboard document (`Dashboard.settings`), not to the
 * browser, so everyone who opens a shared dashboard sees it behave the same way.
 *
 * Ship and range are not here: they are the dashboard's own and are set from
 * the toolbar (`dashboard-context.ts`).
 */

import { html, nothing, type TemplateResult } from 'lit';
import { customElement, state } from 'lit/decorators.js';
import { LightElement } from '../../lit-base.ts';
import { showToast } from '../../ui/toast.ts';
import { StoreController } from '../../state/controller.ts';
import {
  activeDashboard,
  activeDashboardId,
  dashboardDirty,
  dashboards,
  deleteDashboard,
  updateDashboardSettings,
  type Dashboard,
  type DashboardSettings,
} from './dashboard-state.ts';

interface Toggle {
  key: keyof DashboardSettings;
  /** A switch that only means something while this other one is on. */
  needs?: keyof DashboardSettings;
  label: string;
  on: string;
  off: string;
}

const TOGGLES: readonly Toggle[] = [
  {
    key: 'sharedCrosshair',
    label: 'Shared crosshair',
    on: 'Hovering a chart marks the same point on every chart that shares its X axis - usually time.',
    off: 'Each chart shows its own crosshair only.',
  },
  {
    key: 'sharedTooltip',
    needs: 'sharedCrosshair',
    label: 'Shared tooltip',
    on: 'Every chart the crosshair reaches also shows its values at that point.',
    off: 'Only the chart under the pointer shows its values.',
  },
  {
    key: 'showTitles',
    label: 'Chart titles',
    on: 'Each chart shows its title and details above it.',
    off: 'Charts fill their tiles. A tile\'s controls appear on hover; Edit shows the titles again.',
  },
  {
    key: 'tileFrames',
    label: 'Tile frames',
    on: 'Each chart sits on its own card, with an outline.',
    off: 'No cards or outlines: charts sit straight on the page background.',
  },
  {
    key: 'liveSlicers',
    label: 'Filter while dragging',
    on: 'Charts follow a range filter as its handles move, and a list applies each tick at once. Every step reloads the charts on the tab.',
    off: 'Charts update when you let go of a range filter, or press Apply on a list.',
  },
  {
    key: 'floatTiles',
    label: 'Float tiles up',
    on: 'Tiles rise to fill the space above them, so the layout never has gaps.',
    off: 'Tiles stay exactly where you drop them. Gaps are allowed.',
  },
];

@customElement('dashboard-settings')
export class DashboardSettingsEl extends LightElement {
  private store = new StoreController(this, [dashboards, activeDashboardId, dashboardDirty]);

  @state() private confirmDelete = false;
  private confirmTimer: number | null = null;

  override disconnectedCallback(): void {
    super.disconnectedCallback();
    if (this.confirmTimer !== null) window.clearTimeout(this.confirmTimer);
  }

  /** Click, then click again within four seconds - the toolbar's old rule. */
  private onDelete(id: string): void {
    if (!this.confirmDelete) {
      this.confirmDelete = true;
      this.confirmTimer = window.setTimeout(() => {
        this.confirmDelete = false;
      }, 4000);
      return;
    }
    void deleteDashboard(id).then((result) => {
      if (result !== true) showToast('error', 'Could not delete this dashboard.');
    });
  }

  override render(): TemplateResult {
    const d = activeDashboard();
    if (!d) return html``;
    return html`
      <div class="dset">
        ${TOGGLES.map((t) => {
          const on = d.settings[t.key];
          const idle = !!t.needs && !d.settings[t.needs];
          return html`<button
            class="dset-toggle ${on ? 'is-on' : ''}"
            role="switch"
            aria-checked=${on ? 'true' : 'false'}
            ?disabled=${idle}
            @click=${() => updateDashboardSettings({ [t.key]: !on })}
          >
            <span class="dset-toggle-text">
              <strong>${t.label}</strong>
              <span>${idle ? 'Turn on Shared crosshair to use this.' : on ? t.on : t.off}</span>
            </span>
            <span class="sd-switch" aria-hidden="true"></span>
          </button>`;
        })}

        <button
          class="dset-delete ${this.confirmDelete ? 'is-armed' : ''}"
          @click=${() => this.onDelete(d.id)}
        >
          ${this.confirmDelete ? 'Click again to delete this dashboard' : 'Delete dashboard'}
        </button>
      </div>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'dashboard-settings': DashboardSettingsEl;
  }
}
